import React, { useEffect, useState } from "react";
import { useParams, useOutletContext, Link, useSearchParams } from "react-router-dom";
import publicApi from "@/lib/publicApi";
import { useSeo, trackFaq } from "@/lib/useSeo";
import FaqFeedback from "@/components/public/FaqFeedback";
import {
  CalendarHeart, Phone, MessageCircle, ArrowLeft, ArrowRight, ShieldCheck, Loader2, HelpCircle,
} from "lucide-react";

const CONTAINER = "max-w-3xl mx-auto px-4 sm:px-6 lg:px-8";
const telHref = (phone) => `tel:${(phone || "").replace(/[^0-9+]/g, "")}`;

export default function FaqDetail() {
  const { slug } = useParams();
  const { content, openChat } = useOutletContext();
  const [sp] = useSearchParams();
  const franchise = sp.get("franchise") || null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lanes, setLanes] = useState([]);
  const phone = data?.local?.phone || content?.phone;
  const item = data?.item;
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  useSeo({
    title: item ? (item.seo_title || `${item.question} | MovingMyMama FAQ`) : (notFound ? "Answer not found | MovingMyMama" : "Family FAQ | MovingMyMama"),
    description: item ? (item.seo_description || (item.answer || "").slice(0, 155)) : undefined,
    canonical: `${origin}/resources/faq/${slug}`,
    noindex: notFound,
    jsonLd: item ? [{
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: [{ "@type": "Question", name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer } }],
    }] : null,
  });

  useEffect(() => {
    setLoading(true); setNotFound(false);
    publicApi.get("/public/service-lanes").then(({ data }) => setLanes(data.items || [])).catch(() => {});
    publicApi.get(`/public/faq/${slug}`, { params: franchise ? { franchise } : {} })
      .then(({ data }) => { setData(data); trackFaq(publicApi, { event: "question_view", slug, faq_id: data.item?.id, franchise }); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]); // eslint-disable-line

  const laneSlug = (si) => lanes.find((l) => l.service_interest === si)?.slug;

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;
  if (notFound || !item) return (
    <div className={`${CONTAINER} py-24 text-center`} data-testid="faq-detail-notfound">
      <HelpCircle className="mx-auto mb-3 text-[#9AA5B1]" size={30} />
      <h1 className="text-2xl font-display font-bold text-[#1C4358]">We couldn't find that answer</h1>
      <p className="mt-2 text-[16px] text-[#4B5563]">It may have moved. Browse all questions or ask us directly.</p>
      <Link to="/resources/faq" className="mmm-tap mt-5 inline-flex items-center gap-2 px-6 h-12 rounded-full bg-[#1C4358] text-white font-semibold"><ArrowLeft size={16} /> Back to FAQ</Link>
    </div>
  );

  const ls = laneSlug(item.related_service_lane);

  return (
    <div data-testid="faq-detail-page" className="pb-28 lg:pb-16">
      <section className={`${CONTAINER} pt-10`}>
        <Link to="/resources/faq" className="inline-flex items-center gap-1.5 text-[14px] text-[#1C4358] hover:underline"><ArrowLeft size={15} /> All questions</Link>
        {item.category_label && <div className="mt-3 inline-block text-[12px] font-semibold text-[#9C1E22] bg-[#9C1E22]/8 rounded-full px-3 py-1">{item.category_label}</div>}
        <h1 className="mt-3 text-3xl sm:text-4xl font-display font-bold text-[#1C4358] leading-tight">{item.question}</h1>
        <div className="mt-5 text-[18px] text-[#252221] leading-relaxed whitespace-pre-line" data-testid="faq-detail-answer">{item.answer}</div>
        <p className="mt-4 flex items-center gap-1.5 text-[12px] text-[#4B5563]"><ShieldCheck size={13} className="text-[#2F7D5A]" /> General guidance only. A qualified professional should review your family's specific situation.</p>

        <FaqFeedback slug={slug} franchise={franchise} />

        {(ls || item.related_reverse_topic) && (
          <div className="mt-6 flex flex-wrap gap-3">
            {ls && <Link to={`/services/${ls}`} onClick={() => trackFaq(publicApi, { event: "faq_to_service_lane" })} className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-[#1C4358] border border-[#1C4358]/20 rounded-full px-4 h-11 hover:bg-[#1C4358]/6">Explore this service <ArrowRight size={15} /></Link>}
            {item.related_reverse_topic && <Link to={`/find/${item.related_reverse_topic}`} className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-[#1C4358] border border-[#1C4358]/20 rounded-full px-4 h-11 hover:bg-[#1C4358]/6">Learn more <ArrowRight size={15} /></Link>}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className={`${CONTAINER} py-12`}>
        <div className="rounded-2xl bg-[#FBF6EC] border border-[#F0E6CF] p-6 text-center">
          <h2 className="text-xl font-display font-bold text-[#1C4358]">Talk it through with someone who understands</h2>
          <p className="mt-1.5 text-[15px] text-[#4B5563]">Start with a free consultation so your family can understand the next step.</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Link to="/book" data-testid="faq-detail-book-btn" onClick={() => trackFaq(publicApi, { event: "faq_to_booking" })} className="mmm-tap inline-flex items-center gap-2 px-6 h-13 rounded-full bg-[#9C1E22] hover:bg-[#861A1D] text-white font-semibold"><CalendarHeart size={18} /> Book Free Consultation</Link>
            {phone && <a href={telHref(phone)} className="mmm-tap inline-flex items-center gap-2 px-6 h-13 rounded-full border border-[#1C4358]/25 text-[#1C4358] font-semibold hover:bg-[#1C4358]/6"><Phone size={18} /> {phone}</a>}
            <button type="button" onClick={() => { trackFaq(publicApi, { event: "faq_to_chat" }); openChat && openChat({ cta_source: "FAQ Answer Chat" }); }} data-testid="faq-detail-chat-btn" className="mmm-tap inline-flex items-center gap-2 px-6 h-13 rounded-full border border-[#1C4358]/25 text-[#1C4358] font-semibold hover:bg-[#1C4358]/6"><MessageCircle size={18} /> Start Live Chat</button>
          </div>
        </div>
      </section>

      {/* Related */}
      {(data.related || []).length > 0 && (
        <section className={`${CONTAINER} pb-6`}>
          <h2 className="text-lg font-display font-bold text-[#1C4358] mb-3">Related questions</h2>
          <div className="space-y-2">
            {data.related.map((r) => (
              <Link key={r.id} to={`/resources/faq/${r.slug}`} data-testid={`faq-related-${r.slug}`} className="flex items-center justify-between gap-3 rounded-xl border border-[#E6EDF2] bg-white p-4 hover:border-[#1C4358]/40 transition-colors">
                <span className="text-[15px] font-medium text-[#1C4358]">{r.question}</span>
                <ArrowRight size={16} className="text-[#9AA5B1] shrink-0" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Sticky mobile CTA */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#E6EDF2] px-3 py-2 flex gap-2">
        {phone && <a href={telHref(phone)} className="flex-1 mmm-tap inline-flex items-center justify-center gap-1.5 h-11 rounded-full border border-[#1C4358]/25 text-[#1C4358] font-semibold text-[14px]"><Phone size={16} /> Call</a>}
        <button type="button" onClick={() => { trackFaq(publicApi, { event: "faq_to_chat" }); openChat && openChat({ cta_source: "FAQ Answer Chat" }); }} className="flex-1 mmm-tap inline-flex items-center justify-center gap-1.5 h-11 rounded-full border border-[#1C4358]/25 text-[#1C4358] font-semibold text-[14px]"><MessageCircle size={16} /> Chat</button>
        <Link to="/book" onClick={() => trackFaq(publicApi, { event: "faq_to_booking" })} className="flex-[1.3] mmm-tap inline-flex items-center justify-center gap-1.5 h-11 rounded-full bg-[#9C1E22] text-white font-semibold text-[14px]"><CalendarHeart size={16} /> Free Consult</Link>
      </div>
    </div>
  );
}
